import {
	BUDGET_ENTRY_KINDS,
	BUDGET_ENTRY_LIMITS,
	type BudgetEntryCurrency,
	type BudgetEntryDTO,
	type BudgetEntryKind,
	DEFAULT_PROJECT_CURRENCY,
	type ProjectDTO,
	SUPPORTED_PROJECT_CURRENCIES,
} from "@zplanem/responses"
import type React from "react"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import {
	useCreateBudgetEntry,
	useUpdateBudgetEntry,
} from "../../hooks/use-budget"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

/** Add (entry = null) or edit a budget entry. Manual adds land as `confirmed`. */
export function BudgetEntryFormModal({
	propertyId,
	entry,
	projects,
	onClose,
	lockedProjectId,
}: {
	propertyId: string
	entry: BudgetEntryDTO | null
	projects: ProjectDTO[]
	onClose: () => void
	/** Project-scoped view (ZPL-224): entry is pinned to this project and the
	 *  project selector is hidden. */
	lockedProjectId?: string
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const tb = t.projects.budget
	const [amount, setAmount] = useState(entry?.amount ?? "")
	const [currency, setCurrency] = useState<BudgetEntryCurrency>(
		entry?.currency ?? DEFAULT_PROJECT_CURRENCY,
	)
	const [kind, setKind] = useState<BudgetEntryKind | "">(entry?.kind ?? "")
	const [vendor, setVendor] = useState(entry?.vendor ?? "")
	const [description, setDescription] = useState(entry?.description ?? "")
	const [occurredAt, setOccurredAt] = useState(
		entry?.occurredAt ? entry.occurredAt.slice(0, 10) : "",
	)
	const [projectId, setProjectId] = useState(
		lockedProjectId ?? entry?.projectId ?? "",
	)
	const [error, setError] = useState<string | null>(null)

	const create = useCreateBudgetEntry(propertyId)
	const update = useUpdateBudgetEntry(propertyId)
	const pending = create.isPending || update.isPending

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	async function submit(e: React.FormEvent) {
		e.preventDefault()
		setError(null)
		const trimmed = amount.trim()
		if (!BUDGET_ENTRY_LIMITS.amountPattern.test(trimmed)) {
			setError(tb.amountInvalid)
			return
		}
		const shared = {
			amount: trimmed,
			currency,
			kind: kind === "" ? null : kind,
			vendor: vendor.trim() || null,
			description: description.trim() || null,
			projectId: projectId || null,
			occurredAt: occurredAt
				? new Date(`${occurredAt}T12:00:00Z`).toISOString()
				: null,
		}
		try {
			if (entry) {
				await update.mutateAsync({ entryId: entry.id, body: shared })
			} else {
				await create.mutateAsync(shared)
			}
			onClose()
		} catch (err) {
			setError(err instanceof ApiError ? err.message : tb.errorSave)
		}
	}

	const inputClass =
		"w-full rounded-xl bg-muted border border-border px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-ring"

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label={entry ? tb.formTitleEdit : tb.formTitleAdd}
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
		>
			<button
				type="button"
				aria-hidden="true"
				tabIndex={-1}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<form
				onSubmit={submit}
				className="relative w-full max-w-md rounded-lg border border-border bg-popover p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto"
			>
				<h2 className="text-lg font-bold text-foreground">
					{entry ? tb.formTitleEdit : tb.formTitleAdd}
				</h2>

				<div className="flex gap-3">
					<label className="flex-1 flex flex-col gap-1 text-sm text-muted-foreground">
						{tb.amountLabel}
						<input
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							inputMode="decimal"
							placeholder="1250.50"
							className={inputClass}
						/>
					</label>
					<label className="w-28 flex flex-col gap-1 text-sm text-muted-foreground">
						{tb.currencyLabel}
						<select
							value={currency}
							onChange={(e) =>
								setCurrency(e.target.value as BudgetEntryCurrency)
							}
							className={inputClass}
						>
							{SUPPORTED_PROJECT_CURRENCIES.map((c) => (
								<option key={c} value={c}>
									{c}
								</option>
							))}
						</select>
					</label>
				</div>

				<div className="flex gap-3">
					<label className="flex-1 flex flex-col gap-1 text-sm text-muted-foreground">
						{tb.kindLabel}
						<select
							value={kind}
							onChange={(e) => setKind(e.target.value as BudgetEntryKind | "")}
							className={inputClass}
						>
							<option value="">{tb.kindNone}</option>
							{BUDGET_ENTRY_KINDS.map((k) => (
								<option key={k} value={k}>
									{tb.kinds[k]}
								</option>
							))}
						</select>
					</label>
					<label className="flex-1 flex flex-col gap-1 text-sm text-muted-foreground">
						{tb.occurredAtLabel}
						<input
							type="date"
							value={occurredAt}
							onChange={(e) => setOccurredAt(e.target.value)}
							className={inputClass}
						/>
					</label>
				</div>

				<label className="flex flex-col gap-1 text-sm text-muted-foreground">
					{tb.vendorLabel}
					<input
						value={vendor}
						onChange={(e) => setVendor(e.target.value)}
						maxLength={BUDGET_ENTRY_LIMITS.vendorMax}
						className={inputClass}
					/>
				</label>

				<label className="flex flex-col gap-1 text-sm text-muted-foreground">
					{tb.descriptionLabel}
					<textarea
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						maxLength={BUDGET_ENTRY_LIMITS.descriptionMax}
						rows={2}
						className={inputClass}
					/>
				</label>

				{lockedProjectId ? null : (
					<label className="flex flex-col gap-1 text-sm text-muted-foreground">
						{tb.projectLabel}
						<select
							value={projectId}
							onChange={(e) => setProjectId(e.target.value)}
							className={inputClass}
						>
							<option value="">{tb.projectNone}</option>
							{projects.map((p) => (
								<option key={p.id} value={p.id}>
									{p.name}
								</option>
							))}
						</select>
					</label>
				)}

				{error ? (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				) : null}

				<div className="flex justify-end gap-2">
					<Button type="button" variant="ghost" onClick={onClose}>
						{tb.cancel}
					</Button>
					<Button type="submit" disabled={pending || !amount.trim()}>
						{tb.save}
					</Button>
				</div>
			</form>
		</div>
	)
}
